import { ComponentProps, useCallback, useMemo } from 'react';
import React from 'react';

import { StatuspageIcon, GuardIcon } from '@atlaskit/logo';
import { ArtifactsIcon } from '@atlaskit/logo/artifacts/icon';
import { useAnalytics } from '@atlassian/global-shortcuts/analytics';
import { useAssets } from '@atlassian/global-shortcuts/assets';
import { useBitbucket } from '@atlassian/global-shortcuts/bitbucket';
import { useCompanyHub } from '@atlassian/global-shortcuts/company-hub';
import { useCompass } from '@atlassian/global-shortcuts/compass';
import { useConfluence } from '@atlassian/global-shortcuts/confluence';
import { useCustomerServiceManagement } from '@atlassian/global-shortcuts/customer-service-management';
import { useFeedback } from '@atlassian/global-shortcuts/feedback';
import { useFocus } from '@atlassian/global-shortcuts/focus';
import { useGoals } from '@atlassian/global-shortcuts/goals';
import { useJira } from '@atlassian/global-shortcuts/jira';
import { useJiraProductDiscovery } from '@atlassian/global-shortcuts/jira-product-discovery';
import { useJiraServiceManagement } from '@atlassian/global-shortcuts/jira-service-management';
import { useLoom } from '@atlassian/global-shortcuts/loom';
import { useOpsgenie } from '@atlassian/global-shortcuts/opsgenie';
import { useProjects } from '@atlassian/global-shortcuts/projects';
import { useStudio } from '@atlassian/global-shortcuts/studio';
import { useTalent } from '@atlassian/global-shortcuts/talent';
import { useTeams } from '@atlassian/global-shortcuts/teams';
import { useTrello } from '@atlassian/global-shortcuts/trello';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import { useIntl } from 'react-intl';

import { AnalyticsSideNavItem } from '@townsquare/analytics-nav4';
import { useAppsByProduct } from '@townsquare/apps-store/hooks';
import { useCollabContext, useUnitsEnabled } from '@townsquare/collab-context';
import { getConfig } from '@townsquare/config';
import {
	APP_ITEM_ID_TO_WORKLENS,
	SideNavAppItem as SideNavAppItemID,
} from '@townsquare/navigation-config/nav4';
import { useOrgContextOrgId } from '@townsquare/org-context';
import { fg } from '@townsquare/stat-sig/gate';

const COMPANY_HUB_DEFAULT_LABEL = 'Company Hub';

export const useSideNavAppItemsForNavPlatform = (): ComponentProps<
	typeof GlobalSideNavigation
>['shortcuts'] => {
	const config = getConfig();
	const orgId = useOrgContextOrgId();
	const intl = useIntl();
	const collabContext = useCollabContext();
	const products = useAppsByProduct();
	const unitsEnabled = useUnitsEnabled();

	const collabContextCloudIds = useMemo(
		() => collabContext?.collabContextScoping?.collabContextCloudIds ?? [],
		[collabContext?.collabContextScoping?.collabContextCloudIds],
	);

	const [orgHasCompanyHub, companyHubURL, companyHubName] = useMemo(() => {
		return [
			!!products?.COMPANY_HUB,
			products?.COMPANY_HUB?.[0]?.url,
			products?.COMPANY_HUB?.[0]?.productName || '',
		];
	}, [products]);

	const shouldShowShortcut = useCallback(
		(sideNavAppItemId: SideNavAppItemID): boolean => {
			const isUnitFilteringEnabled = unitsEnabled && collabContextCloudIds.length > 0;
			if (!isUnitFilteringEnabled) {
				return true;
			}

			// Trello and Bitbucket are tenantless products, so we always show them
			if (
				sideNavAppItemId === SideNavAppItemID.TRELLO ||
				sideNavAppItemId === SideNavAppItemID.BITBUCKET
			) {
				return true;
			}

			const worklensKey = APP_ITEM_ID_TO_WORKLENS[sideNavAppItemId];
			if (!worklensKey) {
				return false;
			}

			const productSites = products?.[worklensKey];
			if (!productSites || productSites.length === 0) {
				return false;
			}

			return productSites.some((site) => collabContextCloudIds.includes(site.cloudId));
		},
		[products, collabContextCloudIds, unitsEnabled],
	);
	const companyHubItem = useCompanyHub(companyHubURL, { menuItemId: SideNavAppItemID.COMPANY_HUB });

	const jiraItem = useJira('', { menuItemId: SideNavAppItemID.JIRA });
	const confluenceItem = useConfluence('', { menuItemId: SideNavAppItemID.CONFLUENCE });
	const loomItem = useLoom('', { menuItemId: SideNavAppItemID.LOOM });
	const focusItem = useFocus('', { menuItemId: SideNavAppItemID.FOCUS });
	const talentItem = useTalent('', { menuItemId: SideNavAppItemID.TALENT });
	const jiraProductDiscoveryItem = useJiraProductDiscovery('', {
		menuItemId: SideNavAppItemID.JIRA_PRODUCT_DISCOVERY,
	});
	const compassItem = useCompass('', { menuItemId: SideNavAppItemID.COMPASS });
	const bitbucketItem = useBitbucket('', { menuItemId: SideNavAppItemID.BITBUCKET });

	// TODO: Replace with helper from @atlassian/global-shortcuts when it's available.
	const statusPageItem = useMemo(
		() => ({
			apsKey: APP_ITEM_ID_TO_WORKLENS[SideNavAppItemID.STATUSPAGE],
			fallbackUrl: '',
			icon: <StatuspageIcon size="xsmall" />,
			label: intl.formatMessage({
				id: 'townsquare.atlassian-home.side-nav.statuspage',
				description: 'Side nav button to link to Statuspage',
				defaultMessage: 'Statuspage',
			}),
			analyticsName: AnalyticsSideNavItem.STATUSPAGE,
			type: 'link' as const,
			menuItemId: SideNavAppItemID.STATUSPAGE,
		}),
		[intl],
	);

	const jiraServiceManagementItem = useJiraServiceManagement('', {
		menuItemId: SideNavAppItemID.JIRA_SERVICE_MANAGEMENT,
	});
	const assetsItem = useAssets('', { menuItemId: SideNavAppItemID.ASSETS });
	const customerServiceItem = useCustomerServiceManagement('', {
		menuItemId: SideNavAppItemID.JIRA_CUSTOMER_SERVICE,
	});
	const opsgenieItem = useOpsgenie('', { menuItemId: SideNavAppItemID.OPSGENIE });
	const trelloItem = useTrello('', { menuItemId: SideNavAppItemID.TRELLO });

	const teamsItemFallbackUrl = useCallback(
		() => (!orgId ? `${config.fullUrl}/people` : `${config.fullUrl}/o/${orgId}/people`),
		[config.fullUrl, orgId],
	);
	const teamsItem = useTeams(teamsItemFallbackUrl, { menuItemId: SideNavAppItemID.TEAMS_APP });

	const goalsItemFallbackUrl = useCallback(
		() => (!orgId ? `${config.fullUrl}/goals` : `${config.fullUrl}/o/${orgId}/goals`),
		[config.fullUrl, orgId],
	);

	const goalsItem = useGoals(goalsItemFallbackUrl, { menuItemId: SideNavAppItemID.GOALS_APP });

	const projectsItemFallbackUrl = useCallback(
		() => (!orgId ? `${config.fullUrl}/projects` : `${config.fullUrl}/o/${orgId}/projects`),
		[config.fullUrl, orgId],
	);
	const projectsItem = useProjects(projectsItemFallbackUrl, {
		menuItemId: SideNavAppItemID.PROJECTS_APP,
	});

	const studioItem = useStudio('', { menuItemId: SideNavAppItemID.STUDIO });
	const analyticsItem = useAnalytics('', { menuItemId: SideNavAppItemID.ANALYTICS });

	// TODO: Replace with helper from @atlassian/global-shortcuts when it's available.
	const beaconItem = useMemo(
		() => ({
			apsKey: APP_ITEM_ID_TO_WORKLENS[SideNavAppItemID.BEACON],
			fallbackUrl: '',
			icon: <GuardIcon size="xsmall" />,
			label: intl.formatMessage({
				id: 'townsquare.atlassian-home.side-nav.guard-detect',
				description: 'Side nav button to link to Guard Detect',
				defaultMessage: 'Guard Detect',
			}),
			analyticsName: AnalyticsSideNavItem.BEACON,
			type: 'link' as const,
			menuItemId: SideNavAppItemID.BEACON,
		}),
		[intl],
	);

	const feedbackItem = useFeedback('', { menuItemId: SideNavAppItemID.FEEDBACK });

	// TODO: Replace with helper from @atlassian/global-shortcuts when it's available.
	const artifactsItem = useMemo(
		() => ({
			apsKey: APP_ITEM_ID_TO_WORKLENS[SideNavAppItemID.ARTIFACTS],
			fallbackUrl: '',
			icon: <ArtifactsIcon size="xsmall" label="" />,
			label: intl.formatMessage({
				id: 'townsquare.atlassian-home.side-nav.artifacts',
				description: 'Side nav button to link to Artifacts',
				defaultMessage: 'Artifacts',
			}),
			analyticsName: AnalyticsSideNavItem.ARTIFACTS,
			type: 'link' as const,
			menuItemId: SideNavAppItemID.ARTIFACTS,
		}),
		[intl],
	);

	return useMemo(() => {
		const allShortcuts = {
			...(orgHasCompanyHub
				? {
						[SideNavAppItemID.COMPANY_HUB]: {
							...companyHubItem,
							// If the company hub name is not the default, use the custom name
							...(companyHubName !== COMPANY_HUB_DEFAULT_LABEL && { label: companyHubName }),
							analyticsName: AnalyticsSideNavItem.COMPANY_HUB,
						},
					}
				: {}),
			[SideNavAppItemID.JIRA]: { ...jiraItem, analyticsName: AnalyticsSideNavItem.JIRA },
			[SideNavAppItemID.CONFLUENCE]: {
				...confluenceItem,
				analyticsName: AnalyticsSideNavItem.CONFLUENCE,
			},
			[SideNavAppItemID.LOOM]: { ...loomItem, analyticsName: AnalyticsSideNavItem.LOOM },
			[SideNavAppItemID.FOCUS]: { ...focusItem, analyticsName: AnalyticsSideNavItem.FOCUS },
			[SideNavAppItemID.TALENT]: { ...talentItem, analyticsName: AnalyticsSideNavItem.TALENT },
			[SideNavAppItemID.JIRA_PRODUCT_DISCOVERY]: {
				...jiraProductDiscoveryItem,
				analyticsName: AnalyticsSideNavItem.JIRA_PRODUCT_DISCOVERY,
			},
			[SideNavAppItemID.FEEDBACK]: {
				...feedbackItem,
				analyticsName: AnalyticsSideNavItem.FEEDBACK,
			},
			[SideNavAppItemID.COMPASS]: { ...compassItem, analyticsName: AnalyticsSideNavItem.COMPASS },
			[SideNavAppItemID.BITBUCKET]: {
				...bitbucketItem,
				analyticsName: AnalyticsSideNavItem.BITBUCKET,
			},
			[SideNavAppItemID.STATUSPAGE]: statusPageItem,
			[SideNavAppItemID.JIRA_SERVICE_MANAGEMENT]: {
				...jiraServiceManagementItem,
				analyticsName: AnalyticsSideNavItem.JIRA_SERVICE_MANAGEMENT,
			},
			[SideNavAppItemID.ASSETS]: { ...assetsItem, analyticsName: AnalyticsSideNavItem.ASSETS },
			[SideNavAppItemID.JIRA_CUSTOMER_SERVICE]: {
				...customerServiceItem,
				analyticsName: AnalyticsSideNavItem.JIRA_CUSTOMER_SERVICE,
			},
			[SideNavAppItemID.OPSGENIE]: {
				...opsgenieItem,
				analyticsName: AnalyticsSideNavItem.OPSGENIE,
			},
			[SideNavAppItemID.TRELLO]: { ...trelloItem, analyticsName: AnalyticsSideNavItem.TRELLO },
			[SideNavAppItemID.TEAMS_APP]: { ...teamsItem, analyticsName: AnalyticsSideNavItem.TEAMS_APP },
			[SideNavAppItemID.GOALS_APP]: {
				...goalsItem,
				analyticsName: AnalyticsSideNavItem.GOALS,
			},
			[SideNavAppItemID.PROJECTS_APP]: {
				...projectsItem,
				analyticsName: AnalyticsSideNavItem.PROJECTS,
			},
			[SideNavAppItemID.STUDIO]: { ...studioItem, analyticsName: AnalyticsSideNavItem.STUDIO },
			[SideNavAppItemID.ANALYTICS]: {
				...analyticsItem,
				analyticsName: AnalyticsSideNavItem.ANALYTICS,
			},
			[SideNavAppItemID.BEACON]: beaconItem,
			...(fg('townsquare_enable_artifacts_app_full_switcher')
				? { [SideNavAppItemID.ARTIFACTS]: artifactsItem }
				: {}),
		};

		// Filter shortcuts based on collabContext access
		return Object.fromEntries(
			Object.entries(allShortcuts).filter(([key]) => shouldShowShortcut(key as SideNavAppItemID)),
		);
	}, [
		orgHasCompanyHub,
		companyHubItem,
		companyHubName,
		jiraItem,
		confluenceItem,
		loomItem,
		focusItem,
		talentItem,
		jiraProductDiscoveryItem,
		compassItem,
		bitbucketItem,
		statusPageItem,
		jiraServiceManagementItem,
		assetsItem,
		customerServiceItem,
		opsgenieItem,
		trelloItem,
		teamsItem,
		goalsItem,
		projectsItem,
		studioItem,
		analyticsItem,
		beaconItem,
		shouldShowShortcut,
		feedbackItem,
		artifactsItem,
	]);
};
